import { useEffect, lazy, Suspense, useRef } from "react";
import Layout from "../components/layout";
import { Switch, Route, useLocation, Redirect } from "react-router-dom";
import qs from "qs";
import { useCookies } from "react-cookie";
import ContentLoader from "react-content-loader";
import { initTikTokPixel, initRamadanMetaPixel } from "../util/trackingHelper";

// Lazy load ALL pages including ViewAll for code splitting
const ViewAll = lazy(() => import("../pages/ViewAll"));

// Lazy load Home and other pages for code splitting
const Home = lazy(() => import("../pages/home"));

// Lazy load all other pages for code splitting
const BookYourExperience = lazy(() => import("../pages/BookYourExperience"));
const Package = lazy(() => import("../pages/Package"));
const PackageDetail = lazy(() => import("../pages/PackageDetail"));
const PackageCheckout = lazy(() => import("../pages/PackageCheckout"));
const Register = lazy(() => import("../pages/Register"));
const Login = lazy(() => import("../pages/Login"));
const VoucherDetails = lazy(() => import("../pages/VoucherDetails"));
const Upload = lazy(() => import("../pages/Upload"));
const Settings = lazy(() => import("../pages/Settings"));
const Editprofile = lazy(() => import("../pages/Editprofile"));
const OrderHistory = lazy(() => import("../pages/Orderhistory"));
const Vouchers = lazy(() => import("../pages/Vouchers"));
const NearbyVouchers = lazy(() => import("../pages/NearbyVouchers"));
const SearchDetail = lazy(() => import("../pages/SearchDetail"));
const AdventurousDetails = lazy(() => import("../pages/AdventurousDetails"));
const Invoics = lazy(() => import("../pages/Invoics"));
const AllRaffles = lazy(() => import("../pages/AllRaffles"));
const BookingDetails = lazy(() => import("../pages/BookingDetails"));
const AboutUs = lazy(() => import("../pages/AboutUs"));
const ContactUs = lazy(() => import("../pages/ContactUs"));
const OfferDestinations = lazy(() => import("../pages/OfferDestinations"));
const ScratchAndWin = lazy(() => import("../pages/ScratchAndWin"));
const Terms = lazy(() => import("../pages/Terms"));
const Privacy = lazy(() => import("../pages/Privacy"));
const Gallery = lazy(() => import("../pages/Gallery"));
const Cart = lazy(() => import("../pages/Cart"));
const Store = lazy(() => import("../pages/Stores"));
const ProductStore = lazy(() => import("../pages/ProductStore"));
const ProductDetail = lazy(() => import("../pages/ProductDetail"));
const CheckOut = lazy(() => import("../pages/CheckOut"));
const OrderSucess = lazy(() => import("../pages/OrderSucess"));
const Purchasehistory = lazy(() => import("../pages/Purchasehistory"));
const Collection = lazy(() => import("../pages/Collection"));
const Collections = lazy(() => import("../pages/Collections"));
const Tags = lazy(() => import("../pages/Tags"));
const About = lazy(() => import("../pages/About"));
const Products = lazy(() => import("../pages/Products"));
const HosCards = lazy(() => import("../pages/Hos-Cards"));
const MessageHosCards = lazy(() => import("../pages/Messgae-Hos-Cards"));
const Designer = lazy(() => import("../pages/Designer"));
const Designers = lazy(() => import("../pages/Designers"));
const Orders = lazy(() => import("../pages/Orders"));
const EcomOrderDetails = lazy(() => import("../pages/Ecom-Orders"));
const OrderDetails = lazy(() => import("../pages/OrderDetails"));
const OrdersSettings = lazy(() => import("../pages/Settings"));
const EditHosCards = lazy(() => import("../pages/Edit-Hos-Cards"));
const HosCardsOrderHistory = lazy(() =>
  import("../pages/HosCardsOrderHistory")
);
const AboutHosCards = lazy(() => import("../pages/About-Hos-Cards"));
const ContactHosCards = lazy(() => import("../pages/Contact-Hos-Cards"));
const HosCardsOrderDetails = lazy(() =>
  import("../pages/HosCardsOrderDetails")
);

// Loading component for better UX
const PageLoader = () => (
  <div style={{ padding: "20px" }}>
    <ContentLoader
      speed={2}
      width="100%"
      height={400}
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
    >
      <rect x="0" y="0" rx="3" ry="3" width="100%" height="200" />
      <rect x="0" y="220" rx="3" ry="3" width="70%" height="20" />
      <rect x="0" y="250" rx="3" ry="3" width="90%" height="20" />
      <rect x="0" y="280" rx="3" ry="3" width="60%" height="20" />
    </ContentLoader>
  </div>
);

function Routes() {
  const location = useLocation();
  const [cookies, setRefCookie, removeCookie] = useCookies();
  const isFirstRun = useRef(true);

  useEffect(() => {
    const { ref = "" } = qs.parse(location.search.slice(1));
    if (ref.length) setRefCookie("ref", ref);
    var expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 4);
    // console.log(document.referrer);
    if (document.referrer.length) {
      removeCookie("traffic_referral");
      setRefCookie("traffic_referral", document.referrer, {
        expires: expiryDate,
      });
    }
  }, [location.search]);

  // Track page views on route changes
  useEffect(() => {
    // We execute this on every route change including first load to ensure correct pixel is loaded
    initTikTokPixel(location.pathname);
    initRamadanMetaPixel(location.pathname);

    // Skip GTM virtual_page_view on initial load to avoid duplication
    // (GTM "All Pages" trigger handles the initial view)
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    } 

    // Push page view event to dataLayer for GTM
    // COMMENTED OUT to prevent double firing (GTM History Change + this event)
    /*
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "virtual_page_view",
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
    */
  }, [location.pathname, location.search]);

  const currentHostname = window.location.hostname;
  // console.log(currentHostname);

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route exact path="/" component={ViewAll} />
          <Route exact path="/stores/" render={() => <Redirect to="/stores/gsharshop" />} />
          <Route exact path="/home" render={() => <Redirect to="/" />} />
          <Route exact path="/aboutus" component={AboutUs} />
          <Route exact path="/contactus" component={ContactUs} />
          <Route exact path="/d/:url_slug" component={BookYourExperience} />
          <Route
            exact
            path="/book-your-experience"
            component={BookYourExperience}
          />
          <Route exact path="/package" component={Package} />
          <Route exact path="/p/:package_slug" component={PackageDetail} />
          <Route exact path="/package-checkout" component={PackageCheckout} />
          <Route exact path="/v/:voucher_slug" component={VoucherDetails} />
          <Route
            exact
            path="/:settingsType(settings|orders-settings)"
            component={Settings}
          />
          <Route exact path="/register" component={Register} />
          <Route exact path="/login" component={Login} />
          <Route exact path="/voucher-details" component={VoucherDetails} />
          <Route
            exact
            path="/stores/:slug/checkout-address"
            component={CheckOut}
          />
          <Route exact path="/upload" component={Upload} />
          <Route exact path="/edit-profile" component={Editprofile} />
          <Route exact path="/order-history" component={OrderHistory} />
          <Route exact path="/order-payment-complete" component={OrderSucess} />
          <Route exact path="/payment-history" component={Purchasehistory} />
          <Route
            exact
            path="/booking-details/:order_number"
            component={BookingDetails}
          />
          <Route exact path="/vouchers" component={Vouchers} />
          <Route exact path="/vouchers/:url_slug" component={NearbyVouchers} />
          <Route exact path="/s/:t/:d" component={SearchDetail} />
          <Route exact path="/t/:slug" component={AdventurousDetails} />
          <Route exact path="/invoics" component={Invoics} />
          <Route exact path="/all-raffles" component={AllRaffles} />
          <Route exact path="/view-all-destination" component={ViewAll} />
          <Route exact path="/seesharjah" component={OfferDestinations} />
          <Route exact path="/scratch" component={ScratchAndWin} />
          <Route exact path="/terms" component={Terms} />
          <Route exact path="/privacy" component={Privacy} />
          <Route exact path="/gallery" component={Gallery} />
          <Route exact path="/stores/:slug/cart" component={Cart} />
          <Route exact path="/stores" component={Store} />
          <Route exact path="/stores/:slug" component={ProductStore} />
          <Route exact path="/stores/:slug/products" component={Products} />
          <Route
            exact
            path="/stores/:slug/pages/:page_slug"
            component={About}
          />
          <Route exact path="/stores/:slug/tag/:tag_slug" component={Tags} />
          <Route
            exact
            path="/stores/:slug/product_detail/:product_slug"
            component={ProductDetail}
          />
          <Route
            exact
            path="/stores/:slug/collections"
            component={Collections}
          />
          <Route
            exact
            path="/stores/:slug/collections/:collection_slug"
            component={Collection}
          />
          <Route exact path="/stores/:slug/designers" component={Designers} />
          <Route
            exact
            path="/stores/:slug/designers/:designer_slug"
            component={Designer}
          />
          <Route
            exact
            path="/stores/ecom-order-details/:order_number"
            component={EcomOrderDetails}
          />
          <Route
            exact
            path="/stores/orders/:order_number"
            component={EcomOrderDetails}
          />
          <Route exact path="/stores/:slug/orders" component={Orders} />
          <Route
            exact
            path="/stores/:slug/orders/:order_number"
            component={OrderDetails}
          />
          <Route exact path="/hos-postcards" component={HosCards} />
          <Route exact path="/hos-postcards-about" component={AboutHosCards} />
          <Route
            exact
            path="/hos-postcards-contact"
            component={ContactHosCards}
          />
          <Route
            exact
            path="/hos-postcards/edit-my-card/:card_slug"
            component={EditHosCards}
          />
          <Route
            exact
            path="/hos-postcards/message"
            component={MessageHosCards}
          />
          <Route
            exact
            path="/hos-postcards/order-history"
            component={HosCardsOrderHistory}
          />
          <Route
            exact
            path="/hos-postcards/order-details/:order_number"
            component={HosCardsOrderDetails}
          />
        </Switch>
      </Suspense>
    </Layout>
  );
}

export default Routes;
